import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Filter, Play, X, Calendar, Star, ChevronRight, ExternalLink } from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { useCms } from "@/lib/cms";

const resolve = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);
const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_FULL = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Shared cover-image resolver — priority: featured_image → uploaded thumbnail/cover → 
// first gallery image → YouTube thumbnail → Drive image → null (never blank by caller choice)
function getCover(item) {
  if (item.featured_image) return item.featured_image;
  if (item.thumbnail) return item.thumbnail;
  if (item.cover_image) return item.cover_image;
  if (item.images?.[0]) return item.images[0];
  // YouTube thumbnail — derive client-side, no API needed
  if (item.youtube_urls?.[0]) {
    const m = item.youtube_urls[0].match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    if (m) return `https://img.youtube.com/vi/${m[1]}/maxresdefault.jpg`;
  }
  // First Drive image if any
  if (item.drive_links?.[0]) return null; // Drive video — no static thumb, return null
  return null;
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] } }),
};

function getYouTubeId(url) {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

// YouTube thumbnail with automatic quality fallback: maxresdefault → hqdefault → mqdefault → default
function YtImg({ videoId, alt, className }) {
  const chain = ["maxresdefault", "hqdefault", "mqdefault", "default"];
  const [qi, setQi] = useState(0);
  return (
    <img
      src={`https://img.youtube.com/vi/${videoId}/${chain[qi]}.jpg`}
      alt={alt || ""}
      className={className}
      loading="lazy"
      onError={() => setQi(q => Math.min(q + 1, chain.length - 1))}
    />
  );
}

function VideoModal({ src, type, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  let content;
  if (type === "youtube") {
    const vid = getYouTubeId(src);
    content = <iframe src={`https://www.youtube.com/embed/${vid}?autoplay=1&rel=0&modestbranding=1&playsinline=1`} className="w-full aspect-video" allow="autoplay; encrypted-media" allowFullScreen title="Video" />;
  } else if (type === "drive") {
    const embedUrl = src.replace(/\/view.*$/, "/preview");
    content = <iframe src={embedUrl} className="w-full aspect-video" allow="autoplay" allowFullScreen title="Drive video" />;
  } else {
    content = <video src={resolve(src)} controls autoPlay className="w-full aspect-video bg-black" />;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-4xl relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white"><X className="w-6 h-6" /></button>
        <div className="rounded-2xl overflow-hidden bg-black">{content}</div>
      </div>
    </div>
  );
}

function AchievementCard({ item, onClick, index }) {
  const cover = getCover(item);
  const hasVideo = (item.youtube_urls?.length > 0) || (item.drive_links?.length > 0);
  return (
    <motion.div
      variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={index % 6}
      className="group rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer"
      onClick={onClick}
    >
      <div className="relative aspect-[16/10] bg-gray-100 overflow-hidden">
        {cover ? (
          cover.includes("img.youtube.com") ? (
            <YtImg videoId={cover.match(/\/vi\/([^/]+)\//)?.[1]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <img src={resolve(cover)} alt={item.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300"><Star className="w-8 h-8" /></div>
        )}
        {hasVideo && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-white/90 backdrop-blur grid place-items-center shadow-lg group-hover:scale-110 transition-transform">
              <Play className="w-5 h-5 text-gray-800 ml-0.5" fill="currentColor" />
            </div>
          </div>
        )}
        {item.featured && (
          <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold">
            <Star className="w-3 h-3" fill="currentColor" /> Featured
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5">
          <Calendar className="w-3 h-3" />
          {MONTHS[item.achievement_month] || ""} {item.achievement_year}
          <span className="mx-1">·</span>
          {item.category}
        </div>
        <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug">{item.title}</h3>
        {item.subtitle && <p className="text-xs text-gray-500 mt-1 line-clamp-1">{item.subtitle}</p>}
        {item.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {item.tags.slice(0, 3).map((t, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{t}</span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function AchievementDetail({ item, onClose, allItems }) {
  const [videoModal, setVideoModal] = useState(null);
  const related = allItems.filter(a => a.id !== item.id && a.category === item.category).slice(0, 3);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const allMedia = [
    ...(item.images || []).map(u => ({ type: "image", src: u })),
    ...(item.drive_links || []).map(u => ({ type: "drive", src: u })),
    ...(item.youtube_urls || []).map(u => ({ type: "youtube", src: u })),
  ];

  return (
    <>
      {videoModal && <VideoModal src={videoModal.src} type={videoModal.type} onClose={() => setVideoModal(null)} />}
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
        <div className="bg-white w-full max-w-3xl rounded-3xl overflow-hidden my-8" onClick={e => e.stopPropagation()}>
          {/* Cover */}
          {(() => {
            const detailCover = getCover(item);
            return detailCover ? (
              <div className="relative aspect-[2/1] bg-gray-100">
                <img src={resolve(detailCover)} alt={item.title} className="w-full h-full object-cover" />
                <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/40 text-white grid place-items-center hover:bg-black/60"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 text-gray-500 grid place-items-center hover:bg-gray-200"><X className="w-4 h-4" /></button>
            );
          })()}

          <div className="p-6 sm:p-8">

            <div className="flex items-center gap-2 text-xs text-gray-400 uppercase tracking-wider font-medium mb-2">
              <Calendar className="w-3.5 h-3.5" />
              {MONTHS_FULL[item.achievement_month] || ""} {item.achievement_year}
              <span className="mx-1">·</span>
              <span className="text-indigo-600">{item.category}</span>
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug">{item.title}</h2>
            {item.subtitle && <p className="text-sm text-gray-500 mt-1">{item.subtitle}</p>}

            {item.description && (
              <p className="mt-4 text-sm text-gray-600 leading-relaxed whitespace-pre-line">{item.description}</p>
            )}

            {item.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {item.tags.map((t, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium">{t}</span>
                ))}
              </div>
            )}

            {/* Media gallery */}
            {allMedia.length > 0 && (
              <div className="mt-6">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Media</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {allMedia.map((m, i) => (
                    <button key={i} onClick={() => m.type !== "image" ? setVideoModal(m) : null}
                      className="relative aspect-video rounded-xl overflow-hidden bg-gray-100 hover:ring-2 ring-indigo-400 transition group">
                      {m.type === "image" ? (
                        <img src={resolve(m.src)} alt="" className="w-full h-full object-cover" />
                      ) : m.type === "youtube" ? (
                        <>
                          <YtImg videoId={getYouTubeId(m.src)} alt="" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Play className="w-8 h-8 text-white drop-shadow-lg" fill="white" />
                          </div>
                        </>
                      ) : m.type === "drive" ? (
                        <div className="w-full h-full flex items-center justify-center bg-blue-50">
                          <Play className="w-8 h-8 text-blue-500" />
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                          <Play className="w-8 h-8 text-gray-500" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Share */}
            <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-3">
              <button onClick={() => { navigator.clipboard?.writeText(window.location.href); }}
                className="text-xs text-indigo-600 hover:underline flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> Copy link
              </button>
            </div>

            {/* Related */}
            {related.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Related</div>
                <div className="grid grid-cols-3 gap-3">
                  {related.map(r => (
                    <div key={r.id} className="text-center cursor-pointer" onClick={() => { onClose(); setTimeout(() => document.querySelector(`[data-ach="${r.id}"]`)?.click(), 100); }}>
                      <div className="aspect-video rounded-lg overflow-hidden bg-gray-100 mb-1">
                        {getCover(r) ? <img src={resolve(getCover(r))} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
                      </div>
                      <div className="text-[10px] font-medium text-gray-700 line-clamp-1">{r.title}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default function Achievements() {
  const { site } = useCms();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/achievements").then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = ["all", ...new Set(items.map(a => a.category).filter(Boolean))];
  const years = ["all", ...new Set(items.map(a => a.achievement_year).filter(Boolean))].sort((a, b) => b - a);

  let filtered = items.filter(a => {
    if (catFilter !== "all" && a.category !== catFilter) return false;
    if (yearFilter !== "all" && a.achievement_year !== yearFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (a.title || "").toLowerCase().includes(q) || (a.subtitle || "").toLowerCase().includes(q) || (a.tags || []).some(t => t.toLowerCase().includes(q));
    }
    return true;
  });

  if (sort === "oldest") filtered = [...filtered].reverse();
  else if (sort === "featured") filtered = [...filtered].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  const brandName = site?.company_name || "";

  return (
    <div style={{ backgroundColor: "#F8FAFC" }}>
      {selected && <AchievementDetail item={selected} onClose={() => setSelected(null)} allItems={items} />}

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(59,104,231,0.08),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-16">
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-6"
            style={{ backgroundColor: "#3B68E714", color: "#3B68E7" }}>
            <Star className="w-3.5 h-3.5" /> Achievements
          </motion.div>
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="rebrand-serif tracking-tight font-bold leading-[1.05] max-w-3xl"
            style={{ fontSize: "clamp(2.25rem, 4vw + 1rem, 3.5rem)", color: "#1A202C" }}>
            Milestones that define our journey.
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="mt-6 text-base sm:text-lg leading-relaxed max-w-2xl" style={{ color: "#64748B" }}>
            Celebrating every workshop delivered, partnership forged, and teacher empowered{brandName ? ` by ${brandName}` : ""}.
          </motion.p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-0 z-20 bg-white/85 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search achievements…"
              className="w-full pl-9 pr-3 py-2 rounded-full text-sm border border-gray-200 outline-none focus:border-indigo-400" />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
            className="px-3 py-2 rounded-full text-xs font-medium bg-white border border-gray-200">
            {categories.map(c => <option key={c} value={c}>{c === "all" ? "All Categories" : c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
          <select value={yearFilter} onChange={e => setYearFilter(e.target.value === "all" ? "all" : parseInt(e.target.value))}
            className="px-3 py-2 rounded-full text-xs font-medium bg-white border border-gray-200">
            {years.map(y => <option key={y} value={y}>{y === "all" ? "All Years" : y}</option>)}
          </select>
          <select value={sort} onChange={e => setSort(e.target.value)}
            className="px-3 py-2 rounded-full text-xs font-medium bg-white border border-gray-200">
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="featured">Featured</option>
          </select>
          <span className="text-xs text-gray-500 ml-auto">{filtered.length} results</span>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(n => <div key={n} className="h-64 rounded-2xl bg-gray-100 animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Star className="w-8 h-8 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No achievements found.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item, i) => (
              <div key={item.id} data-ach={item.id}>
                <AchievementCard item={item} index={i} onClick={() => setSelected(item)} />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
